"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Sun, Sparkles, CloudSun } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Seasonality & Wedding Compression",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage sharp peak winter surges and multi-day royal wedding compression against deep summer dips. We build dynamic minimum length-of-stay controls and tiered pacing so your Jodhpur property never relies on panic discounting.",
  metricLabel: "Strategy focus",
  metricValue: "Pace-based pricing & wedding blackout fences"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track intraday rate shifts across MakeMyTrip, Agoda, and Booking.com among luxury palaces, heritage havelis, and boutique stays. Automated competitive alerts protect your RevPAR while capturing high-intent bookings ahead of market drops.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & rule-based rate fencing"
}, {
  id: 3,
  title: "The OTA Commission & Parity Trap",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Stop unauthorized B2B wholesaler leakage and reclaim profit margins lost to steep OTA commissions. We enforce strict rate parity across Google Hotel Ads and drive direct guest conversions through targeted experiential package incentives.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct booking engine boost"
}];
const seasons = [{
  id: 1,
  period: "October – March",
  name: "Peak Heritage & Royal Wedding Season",
  badge: "High ADR & Max RevPAR",
  icon: Sparkles,
  focus: "International leisure, RIFF & Marwar festivals, luxury destination weddings.",
  tactic: "Strict MLOS (2-3 nights), non-refundable advance purchase rates, and premium suite yield fencing."
}, {
  id: 2,
  period: "July – September",
  name: "Monsoon & Shoulder Window",
  badge: "Domestic Leisure & Advance Inquiries",
  icon: CloudSun,
  focus: "Regional drive-in leisure from Gujarat & NCR, heritage weekend getaways.",
  tactic: "Experiential dinner packages, value-add inclusions, and direct booking incentives."
}, {
  id: 3,
  period: "April – June",
  name: "Summer Value & MICE Season",
  badge: "Occupancy & Base Load Protection",
  icon: Sun,
  focus: "Corporate retreats, staycations, local banquet events, and transiting travelers.",
  tactic: "Unbundled base rates, flexible cancellation policies, and daytime event cross-selling."
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapejodhpur" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Jodhpur
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Jodhpur presents a distinctive heritage and desert hospitality market marked by high-yield winter peaks, intense wedding destination compression, and extended summer troughs. Sunil Singh designs tailored revenue architectures built specifically for heritage havelis, luxury palaces, and boutique hotels in the Blue City.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16 md:mb-20">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.15
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="rounded-xl border border-border bg-card/60 p-6 md:p-8 lg:p-10 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 pb-6 border-b border-border/70">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                Jodhpur Demand Cycles
              </span>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mt-1">
                Seasonal Yield Strategy Matrix
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Precise rate structures and inventory allocation for every phase of Jodhpur’s tourism calendar.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasons.map((season, index) => {
            const IconComp = season.icon;
            return <div key={season.id} data-index={index} className="flex flex-col justify-between rounded-lg border border-border/80 bg-background/70 p-5">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span className="inline-flex items-center gap-1.5 text-xs font-mono font-medium text-secondary">
                        <IconComp className="size-4" />
                        {season.period}
                      </span>
                      <span className="text-[11px] font-medium bg-secondary/10 text-secondary-foreground px-2 py-0.5 rounded-md">
                        {season.badge}
                      </span>
                    </div>
                    <h4 className="font-serif text-lg font-bold text-foreground mb-2">
                      {season.name}
                    </h4>
                    <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                      {season.focus}
                    </p>
                  </div>
                  <div className="rounded-md border border-border/60 bg-muted/30 p-3 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      Revenue Tactic:
                    </p>
                    <p className="text-muted-foreground">
                      {season.tactic}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Jodhpur hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}